<script lang="ts">
    import Icon from '@iconify/svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { intToVisual } from 'hydria/utils/numbers'

    import InputIntCurrency from 'hydria/components/InputIntCurrency.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    interface Props {
        userId?: string | null
    }

    let { userId = null }: Props = $props()

    let useGlobalPrice = $state(true)

    let savedPrice: number | undefined = $state()
    let editedPrice: number | undefined = $state()
    let effPrice: number | undefined = $derived(useGlobalPrice == true ? undefined : editedPrice)

    let priceOk = $derived((effPrice != null && effPrice > 0 && effPrice < 10000) || (effPrice == null && userId != null))

    let changed = $derived(savedPrice !== effPrice)

    let loading = $state(true)
    let saving = $state(false)
    let error = $state(false)
    let loadedUserId: string | undefined | null = $state()

    async function load() {
        const userIdToLoad = userId ? userId : ''
        if (loadedUserId == userIdToLoad) return
        loadedUserId = userIdToLoad
        loading = true

        try {
            const r = await simpleFetch('GET', `/json/ecom/1/zone-prices/${userId ? userId : ''}`)
            savedPrice = r.data.published_price != null ? r.data.published_price : null
            useGlobalPrice = savedPrice == null
            editedPrice = savedPrice
        } catch (e: any) {
            console.error('Failed to load zone price:', e)
            if (e && e.status == 404) {
                // This is OK, the user simply doesn’t have this data yet.
                savedPrice = undefined
                useGlobalPrice = true
                editedPrice = savedPrice
            } else {
                notify({
                    type: 'danger',
                    title: 'Zone',
                    text: 'Le prix actuel n’a pas pu être chargé suite à une erreur.',
                })
                error = true
            }
        }

        loading = false
    }

    async function save() {
        saving = true

        try {
            const r = await simpleFetch('PUT', `/json/ecom/1/zone-prices/${userId ? userId : ''}`, { published_price: effPrice })
            savedPrice = r.data.published_price ? r.data.published_price : null
            useGlobalPrice = savedPrice == null
            editedPrice = savedPrice
            notify({
                type: 'success',
                title: 'Zone',
                text: `Le nouveau prix de ${savedPrice != null ? intToVisual(savedPrice) : '(valeur globale)'} est maintenant actif.`,
            })
        } catch (e: any) {
            console.error('Failed to save zone price:', e)
            let msg = 'Le prix de zone n’a pas pu être sauvegardé suite à une erreur.'
            if (e.data && e.data.ecode == 'SUBACCOUNT') {
                msg = 'Ce compte est un sous-compte.'
            }
            notify({
                type: 'danger',
                title: 'Zone',
                text: msg,
            })
            error = true
        }
        saving = false
    }

    $effect(() => {
        load()
    })
    // To help detect the checkbox change
    $effect(() => {
        if (!useGlobalPrice && editedPrice == null) editedPrice = 0
    })
</script>

<div class="card">
    <div class="card-title text-primary">{$_('Zones')}</div>
    <div class="card-body">
        <form
            class="d-grid gap-2"
            onsubmit={(e) => {
                e.preventDefault()
                save()
            }}
            novalidate
        >
            <div class="row">
                <label for="publishedZonePrice" class="col-sm-3 col-form-label">{$_('Prix d’une zone publiable')}</label>
                {#if loading}
                    <div class="col-sm-9">
                        <LoadingPattern pattern={[['flex', ['b:0 0 18em', '1 0 0']]]} />
                    </div>
                {:else if error}
                    <div class="col-sm-9 text-danger">L’information n’a pas pu être chargée.</div>
                {:else}
                    <div class="col-sm-9" style="display: flex; gap: 1em;">
                        {#if userId}
                            <div>
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tld-zp-default" bind:group={useGlobalPrice} value={true} /> Utiliser le prix global
                                    </label>
                                </div>
                                <div class="radio">
                                    <label>
                                        <input type="radio" name="tld-zp-default" bind:group={useGlobalPrice} value={false} style="margin-top: 7px;" /> Utiliser le prix suivant :
                                        <div style="display: inline-block; width: 9em; vertical-align: middle;">
                                            <div class="input-group input-group-sm">
                                                <InputIntCurrency class="form-control {priceOk ? '' : 'is-invalid'}" bind:value={editedPrice} disabled={useGlobalPrice} min={0} max={10000} />
                                                <span class="input-group-text">$</span>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        {:else}
                            <div>
                                <span class="input-group" style="width: 10em;">
                                    <InputIntCurrency class="form-control {priceOk ? '' : 'is-invalid'}" bind:value={editedPrice} id="publishedZonePrice" min={1} max={10000} step={1} />
                                    <span class="input-group-text">$</span>
                                </span>
                            </div>
                        {/if}
                        <div>
                            {#if saving}
                                <button type="button" class="btn btn-info" disabled aria-hidden="true"><span class="spinner-border spinner-border-sm" role="status"></span></button>
                            {:else}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    disabled={!changed}
                                    onclick={() => {
                                        useGlobalPrice = savedPrice == null
                                        editedPrice = savedPrice
                                    }}><Icon inline icon="fa:undo" /></button
                                >
                                <button type="submit" class="btn btn-outline-secondary" disabled={!priceOk || !changed}>{$_('Save')}</button>
                            {/if}
                        </div>
                    </div>
                {/if}
            </div>
        </form>
    </div>
</div>
